import { Row, Table } from '@tanstack/react-table';
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import React, { useCallback, useEffect } from 'react';

import { useRemToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { CardConfig } from '@work4all/models/lib/table-schema/card-config';

import { ICssClasses } from '../../types';
import { SEPARATOR_ROW_HEIGHT } from '../row-render/components/SeparatorRow/SeparatorRow';
import { VirtualizedRow } from '../row-render/VirtualizedRowV8';

export interface RowVirtualizerV8Props {
  tableContainerRef: React.RefObject<HTMLDivElement>;
  table: Table<any>;
  cardsView: boolean;
  cardConfig: CardConfig | null;
  fetchMore: (
    startIndex: number,
    endIndex: number,
    rows: Row<unknown>[]
  ) => Promise<void> | null | undefined;
  onRowDoubleClick?: (id: string) => void;
  onRowClick?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    row: Row<unknown>
  ) => void;
  onRowContextMenu?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    row: Row<unknown>
  ) => void;
  classes?: ICssClasses;
  /**
   * @default "2.25rem"
   */
  rowHeightRem?: string;

  /**
   * On mobile devices we show a toolbar at the bottom of the container. Pass
   * the toolbar's height here ot add as much bottom padding to the table
   * container, so the toolbar does not obstruct the last row.
   */
  bottomPadding?: string | null;
  ignoreLongPress?: boolean;
  /**
   * Allow rows to auto-adjust their height based on content
   */
  allowAutoHeight?: boolean;
  virtualizerRef?: React.RefObject<Virtualizer<HTMLDivElement, HTMLDivElement>>;
}

const DEFAULT_ROW_HEIGHT = '2.25rem';

export function RowVirtualizerV8(props: RowVirtualizerV8Props) {
  const {
    table,
    tableContainerRef,
    rowHeightRem = DEFAULT_ROW_HEIGHT,
    bottomPadding = 0,
    cardConfig,
    cardsView,
    virtualizerRef,
    fetchMore,
  } = props;

  const rowHeightPx = useRemToPx(rowHeightRem);
  const paddingEnd = useRemToPx(bottomPadding);

  const isCardsView = Boolean(cardsView && cardConfig);

  const rows = table.getRowModel().rows;

  const itemSize = useCallback(
    (index: number) => {
      const row = rows[index];
      const isSectionStart = row?.original.meta?.isSectionStart ?? false;
      return isSectionStart ? rowHeightPx + SEPARATOR_ROW_HEIGHT : rowHeightPx;
    },
    [rows, rowHeightPx]
  );

  const getItemKey = useCallback(
    (index: number) => {
      const prefix = isCardsView ? 'card' : 'row';
      const id = rows[index]?.id || index;
      return `${prefix}-${id}`;
    },
    [isCardsView, rows]
  );

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
    count: table.getRowCount(),
    getScrollElement: () => tableContainerRef.current,
    // For some reason when scrolling down the element is not visible. Add some
    // padding to make sure it's on screen.
    scrollPaddingEnd: 64,
    measureElement(element) {
      const height = element.getBoundingClientRect().height;

      return element.dataset.sectionStart === 'true'
        ? height + SEPARATOR_ROW_HEIGHT
        : height;
    },
    getItemKey: getItemKey,
    estimateSize: itemSize,
    paddingEnd: paddingEnd,
    overscan: 1,
  });

  useEffect(() => {
    if (virtualizerRef) {
      virtualizerRef.current = rowVirtualizer;
    }
  }, [rowVirtualizer, virtualizerRef]);

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    virtualItems.forEach((item) => {
      const index = item.index;
      const row = rows[index];

      const isItemLoaded = !!row && !row.original.skeleton;

      if (!isItemLoaded) {
        fetchMore(index, -1, rows);
      }
    });
  }, [fetchMore, rows, virtualItems]);

  return (
    <div
      style={{
        outline: 'none',
        display: 'grid',
        position: 'relative',
        height: `${rowVirtualizer.getTotalSize()}px`,
        width: isCardsView ? '100%' : table.getTotalSize(),
        minWidth: '100%',
      }}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index];
        return (
          <VirtualizedRow
            key={virtualRow.key}
            height={rowHeightPx}
            row={row}
            virtualRow={virtualRow}
            rowVirtualizer={rowVirtualizer}
            isUnderPressSelect={table.options.isUnderPressSelect}
            setUnderPressSelect={table.options.setUnderPressSelect}
            {...props}
          />
        );
      })}
    </div>
  );
}
